import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import IdeasForm from './IdeasForm';
import AdminUserManagement from './AdminUserManagement';
import JourneyConfigForm from './JourneyConfigForm';
import FacilitatorJourneyDetail from './FacilitatorJourneyDetail';
import JourneyOverviewReport from './JourneyOverviewReport';
import ProfilePage from './ProfilePage';
import ProfileDropdown from './ProfileDropdown';
import '../FacilitatorJourneyDetail.css';

const API_URL = 'http://localhost:5000';
const ITEMS_PER_PAGE = 12;

function AdminDashboard({ token, userId, userName, userRole, onLogout }) {
  const [showProfile, setShowProfile] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [teamMembers, setTeamMembers] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notesContent, setNotesContent] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [systemStats, setSystemStats] = useState({
    totalJourneys: 0,
    totalUsers: 0,
    totalIdeas: 0,
    activeJourneys: 0,
  });

  // Score weighting state
  const [scoreWeights, setScoreWeights] = useState({
    impact_weight: 0.33,
    feasibility_weight: 0.33,
    novelty_weight: 0.34,
  });
  const [savingWeights, setSavingWeights] = useState(false);
  const [weightsSaved, setWeightsSaved] = useState(false);

  // Journey state
  const [allJourneys, setAllJourneys] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('updated');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchDashboardData();
  }, [token, userId]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch all users for team members display
      const usersRes = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTeamMembers(usersRes.data.users || []);

      // Fetch all journeys
      const journeysRes = await axios.get(`${API_URL}/api/journeys`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Fetched journeys:', journeysRes.data.journeys);
      setAllJourneys(journeysRes.data.journeys || []);

      // Set system stats
      setSystemStats({
        totalJourneys: journeysRes.data.journeys?.length || 0,
        totalUsers: usersRes.data.users?.length || 0,
        totalIdeas: 0,
        activeJourneys: (journeysRes.data.journeys || []).filter(j => j.status === 'active').length,
      });
      setCurrentPage(1);
    } catch (err) {
      console.error('Fetch dashboard data error:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredAndSortedJourneys = () => {
    let filtered = [...allJourneys];

    // Filter by search term (title match)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(journey =>
        journey.title.toLowerCase().includes(term)
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(journey => journey.status === statusFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.title.localeCompare(b.title);
        case 'updated':
          return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
        case 'created':
          return new Date(b.created_at) - new Date(a.created_at);
        default:
          return 0;
      }
    });

    // Calculate pagination
    const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedResults = filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    return {
      filtered,
      paginatedResults,
      totalPages,
      totalCount: filtered.length,
    };
  };

  const handleSaveNotes = async () => {
    try {
      setSavingNotes(true);
      // Save to localStorage for now
      localStorage.setItem('adminNotes', notesContent);
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 3000);
      setError('');
    } catch (err) {
      console.error('Save notes error:', err);
      setError('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleWeightChange = (weightType, value) => {
    const numValue = Math.max(0, Math.min(1, parseFloat(value)));
    setScoreWeights({
      ...scoreWeights,
      [weightType]: numValue,
    });
  };

  const handleNormalizeWeights = () => {
    const total = scoreWeights.impact_weight + scoreWeights.feasibility_weight + scoreWeights.novelty_weight;
    if (total > 0) {
      setScoreWeights({
        impact_weight: scoreWeights.impact_weight / total,
        feasibility_weight: scoreWeights.feasibility_weight / total,
        novelty_weight: scoreWeights.novelty_weight / total,
      });
    }
  };

  const handleSaveWeights = async () => {
    try {
      setSavingWeights(true);
      // Save to localStorage for now
      localStorage.setItem('scoreWeights', JSON.stringify(scoreWeights));
      setWeightsSaved(true);
      setTimeout(() => setWeightsSaved(false), 3000);
      setError('');
    } catch (err) {
      console.error('Save weights error:', err);
      setError('Failed to save weights');
    } finally {
      setSavingWeights(false);
    }
  };

  if (loading) {
    return (
      <div className="facilitator-journey-detail">
        <div className="loading">Loading admin dashboard...</div>
      </div>
    );
  }

  // Show journey detail if one is selected
  if (selectedJourney) {
    const journey = allJourneys.find(j => j.id === selectedJourney);
    if (journey) {
      return (
        <FacilitatorJourneyDetail
          journey={journey}
          token={token}
          userRole="admin"
          onBack={() => {
            setSelectedJourney(null);
            fetchDashboardData();
          }}
          onTeamUpdate={fetchDashboardData}
        />
      );
    }
  }

  if (showProfile) {
    return (
      <ProfilePage
        token={token}
        onBack={() => setShowProfile(false)}
      />
    );
  }

  return (
    <div className="facilitator-journey-detail">
      {/* Header */}
      <div className="detail-header">
        <div>
          <h1>Admin Dashboard</h1>
          <p className="subtitle">System administration and management</p>
        </div>
        <ProfileDropdown
          userName={userName}
          userRole={userRole}
          onLogout={onLogout}
          onNavigateToProfile={() => setShowProfile(true)}
        />
      </div>

      {error && <div className="error-banner">{error}</div>}
      {notesSaved && <div className="success-banner">Notes saved successfully</div>}

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            📊 Overview
          </button>
          <button
            className={`tab ${activeTab === 'journeys' ? 'active' : ''}`}
            onClick={() => setActiveTab('journeys')}
          >
            🗂️ Journeys
          </button>
          <button
            className={`tab ${activeTab === 'ideas' ? 'active' : ''}`}
            onClick={() => setActiveTab('ideas')}
          >
            💡 Ideas
          </button>
          <button
            className={`tab ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            📝 Notes
          </button>
          <button
            className={`tab ${activeTab === 'discussions' ? 'active' : ''}`}
            onClick={() => setActiveTab('discussions')}
          >
            💬 Discussions
          </button>
          <button
            className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            ⚙️ Settings
          </button>
          <button
            className={`tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            👥 User Management
          </button>
          <button
            className={`tab ${activeTab === 'reports' ? 'active' : ''}`}
            onClick={() => setActiveTab('reports')}
          >
            📊 Reports
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="tab-panel overview-panel">
            <div className="overview-section">
              <h3>System Overview</h3>
              <div className="overview-grid">
                <div className="overview-item">
                  <label>Total Users</label>
                  <p className="stat-value">{systemStats.totalUsers}</p>
                </div>
                <div className="overview-item">
                  <label>Total Journeys</label>
                  <p className="stat-value">{systemStats.totalJourneys}</p>
                </div>
                <div className="overview-item">
                  <label>Active Journeys</label>
                  <p className="stat-value">{systemStats.activeJourneys}</p>
                </div>
                <div className="overview-item">
                  <label>Total Ideas</label>
                  <p className="stat-value">{systemStats.totalIdeas}</p>
                </div>
              </div>
            </div>

            <div className="overview-section">
              <h3>System Users ({teamMembers.length})</h3>
              {teamMembers.length === 0 ? (
                <p className="empty">No users found</p>
              ) : (
                <div className="team-list">
                  {teamMembers.slice(0, 10).map(member => (
                    <div key={member.id} className="team-member">
                      <div className="member-info">
                        <div className="member-name">{member.name}</div>
                        <div className="member-email">{member.email}</div>
                        <div className="member-role">
                          <span className={`role-badge ${member.role}`}>{member.role}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Journeys Tab */}
        {activeTab === 'journeys' && (
          <div className="tab-panel journeys-panel">
            <div className="journeys-section">
              <div className="journeys-header">
                <h3>Innovation Journeys</h3>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowCreateForm(!showCreateForm)}
                >
                  {showCreateForm ? '✕ Cancel' : '+ Create New Journey'}
                </button>
              </div>

              {showCreateForm && (
                <div className="create-journey-form">
                  <h4>Create New Journey</h4>
                  <JourneyConfigForm
                    token={token}
                    userId={userId}
                    onStartJourney={() => {
                      setShowCreateForm(false);
                      fetchDashboardData();
                    }}
                    onViewAllJourneys={() => {
                      setShowCreateForm(false);
                      fetchDashboardData();
                    }}
                  />
                </div>
              )}

              <div className="journeys-list">
                <h4>All Journeys ({allJourneys.length})</h4>
                {allJourneys.length === 0 ? (
                  <div className="empty-state">
                    <p>No journeys created yet. Click "Create New Journey" to get started.</p>
                  </div>
                ) : (
                  <>
                    {/* Search, Filter, Sort Bar */}
                    <div className="search-filter-bar">
                      <div className="search-input-wrapper">
                        <input
                          type="text"
                          placeholder="Search journeys by title..."
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                          }}
                        />
                      </div>

                      {/* Filter Pills */}
                      <div className="filter-pills">
                        <label>Status:</label>
                        <button
                          className={`filter-pill ${statusFilter === 'all' ? 'active' : ''}`}
                          onClick={() => {
                            setStatusFilter('all');
                            setCurrentPage(1);
                          }}
                        >
                          All
                        </button>
                        <button
                          className={`filter-pill ${statusFilter === 'active' ? 'active' : ''}`}
                          onClick={() => {
                            setStatusFilter('active');
                            setCurrentPage(1);
                          }}
                        >
                          Active
                        </button>
                        <button
                          className={`filter-pill ${statusFilter === 'paused' ? 'active' : ''}`}
                          onClick={() => {
                            setStatusFilter('paused');
                            setCurrentPage(1);
                          }}
                        >
                          Paused
                        </button>
                      </div>

                      {/* Sort and Results Counter */}
                      <div className="sort-results-container">
                        <div className="sort-select-wrapper">
                          <label htmlFor="sort-select">Sort by:</label>
                          <select
                            id="sort-select"
                            className="sort-select"
                            value={sortBy}
                            onChange={(e) => {
                              setSortBy(e.target.value);
                              setCurrentPage(1);
                            }}
                          >
                            <option value="updated">Recently Updated</option>
                            <option value="created">Recently Created</option>
                            <option value="name">Name (A-Z)</option>
                          </select>
                        </div>
                        <div className="results-counter">
                          {(() => {
                            const { totalCount, paginatedResults } = getFilteredAndSortedJourneys();
                            return `Showing ${paginatedResults.length > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0} - ${(currentPage - 1) * ITEMS_PER_PAGE + paginatedResults.length} of ${totalCount}`;
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Journeys Grid */}
                    {(() => {
                      const { paginatedResults, totalPages, totalCount } = getFilteredAndSortedJourneys();
                      return (
                        <>
                          {totalCount === 0 ? (
                            <div className="empty-state">
                              <p>No journeys match your search criteria</p>
                            </div>
                          ) : (
                            <>
                              <div className="journeys-grid">
                                {paginatedResults.map(journey => (
                      <div
                        key={journey.id}
                        className="journey-card"
                        onClick={() => setSelectedJourney(journey.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="journey-card-header">
                          <h5>{journey.title}</h5>
                          <span className={`status-badge ${journey.status}`}>
                            {journey.status.toUpperCase()}
                          </span>
                        </div>
                        {journey.description && (
                          <p className="journey-description">{journey.description}</p>
                        )}
                        <div className="journey-meta">
                          <div className="meta-item">
                            <span className="meta-label">Stage:</span>
                            <span className="meta-value">{journey.current_stage}</span>
                          </div>
                          <div className="meta-item">
                            <span className="meta-label">Team:</span>
                            <span className="meta-value">{journey.team_count} members</span>
                          </div>
                          <div className="meta-item">
                            <span className="meta-label">Ideas:</span>
                            <span className="meta-value">{journey.ideas_count}</span>
                          </div>
                        </div>
                      </div>
                                ))}
                              </div>

                              {/* Pagination */}
                              {totalPages > 1 && (
                                <div className="pagination">
                                  <button
                                    className="pagination-button"
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                  >
                                    ← Previous
                                  </button>
                                  <div className="pagination-info">
                                    Page {currentPage} of {totalPages}
                                  </div>
                                  <button
                                    className="pagination-button"
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                  >
                                    Next →
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Ideas Tab */}
        {activeTab === 'ideas' && (
          <div className="tab-panel ideas-panel">
            <h3>System Ideas</h3>
            <p className="empty">Ideas view coming soon</p>
          </div>
        )}

        {/* Notes Tab */}
        {activeTab === 'notes' && (
          <div className="tab-panel notes-panel">
            <div className="notes-section">
              <h3>Admin Notes</h3>
              <textarea
                className="notes-textarea"
                value={notesContent}
                onChange={(e) => setNotesContent(e.target.value)}
                placeholder="Add admin notes here..."
              />
              <button
                className="btn btn-primary"
                onClick={handleSaveNotes}
                disabled={savingNotes}
              >
                {savingNotes ? 'Saving...' : 'Save Notes'}
              </button>
              {notesSaved && <div className="success-message">✓ Notes saved</div>}
            </div>
          </div>
        )}

        {/* Discussions Tab */}
        {activeTab === 'discussions' && (
          <div className="tab-panel discussions-panel">
            <h3>System Discussions</h3>
            <p className="empty">Discussions view coming soon</p>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="tab-panel settings-panel">
            <div className="settings-section">
              <h3>Idea Scoring Weights</h3>
              <p className="settings-description">
                Adjust how heavily each scoring dimension is weighted when ranking ideas. Higher weights have more influence on the final score.
              </p>

              <div className="weight-controls">
                <div className="weight-group">
                  <div className="weight-header">
                    <label htmlFor="impact-weight">Impact Weight</label>
                    <span className="weight-value">{(scoreWeights.impact_weight * 100).toFixed(0)}%</span>
                  </div>
                  <input
                    id="impact-weight"
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={scoreWeights.impact_weight}
                    onChange={(e) => handleWeightChange('impact_weight', e.target.value)}
                    className="weight-slider"
                  />
                  <p className="weight-description">Measures potential positive impact on business/users</p>
                </div>

                <div className="weight-group">
                  <div className="weight-header">
                    <label htmlFor="feasibility-weight">Feasibility Weight</label>
                    <span className="weight-value">{(scoreWeights.feasibility_weight * 100).toFixed(0)}%</span>
                  </div>
                  <input
                    id="feasibility-weight"
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={scoreWeights.feasibility_weight}
                    onChange={(e) => handleWeightChange('feasibility_weight', e.target.value)}
                    className="weight-slider"
                  />
                  <p className="weight-description">Measures technical/practical feasibility to implement</p>
                </div>

                <div className="weight-group">
                  <div className="weight-header">
                    <label htmlFor="novelty-weight">Novelty Weight</label>
                    <span className="weight-value">{(scoreWeights.novelty_weight * 100).toFixed(0)}%</span>
                  </div>
                  <input
                    id="novelty-weight"
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={scoreWeights.novelty_weight}
                    onChange={(e) => handleWeightChange('novelty_weight', e.target.value)}
                    className="weight-slider"
                  />
                  <p className="weight-description">Measures how novel/innovative the idea is</p>
                </div>
              </div>

              <div className="weight-total">
                <label>Total Weight</label>
                <div className="weight-total-bar">
                  <div
                    className="weight-total-fill"
                    style={{ width: `${(scoreWeights.impact_weight + scoreWeights.feasibility_weight + scoreWeights.novelty_weight) * 100}%` }}
                  />
                </div>
                <span className="weight-total-value">
                  {((scoreWeights.impact_weight + scoreWeights.feasibility_weight + scoreWeights.novelty_weight) * 100).toFixed(0)}%
                </span>
              </div>

              <div className="weight-actions">
                <button
                  className="btn btn-secondary"
                  onClick={handleNormalizeWeights}
                >
                  Normalize (Sum to 100%)
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveWeights}
                  disabled={savingWeights}
                >
                  {savingWeights ? 'Saving...' : 'Save Weights'}
                </button>
              </div>

              {weightsSaved && <div className="success-message">✓ Weights saved successfully</div>}
            </div>
          </div>
        )}

        {/* User Management Tab */}
        {activeTab === 'users' && (
          <div className="tab-panel users-panel" style={{ padding: '0' }}>
            <AdminUserManagement
              token={token}
              onLogout={onLogout}
              isEmbedded={true}
            />
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="tab-panel reports-panel">
            <JourneyOverviewReport token={token} />
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
